import { useEffect, useRef, useState } from 'react';
import { GoAModal, GoASpacer, GoASpinner } from '@abgov/react-components';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import {
  fetchConfiguration,
  isHitAndRun,
} from '../../state/slices/configurationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { CollisionTypeComponent } from '../collision-identification/collision-type-component';
import { AnimalComponet } from '../collision-identification/animal-component';
import { ParkedHitAndRunComponent } from '../collision-identification/parked-hit-and-run-component';
import { ObjectOnRoadwayComponent } from '../collision-identification/object-on-roadway';
import { SideOfRoadComponent } from '../collision-identification/side-of-road-component';
import { NotificationType } from '../notifications/notification-component';
import { AppDispatch, RootState } from '../../state/store';
import { useCsrService } from '../../services/csr-api-redux';
import { publishCollision } from '../../state/slices/collisionSlice';
import { DamageComponent } from '../damage/damage-component';
import { DamageStickerComponent } from '../damage/damage-sticker-component';
import { EligibilityComponent } from '../eligibility/eligibility-component';
import { EligibilitySuccessComponent } from '../eligibility/eligibility-success';
import { AttachmentTypeComponent } from '../attachment/attachment-type-component';
import { CarryingLoadComponent } from '../attachment/carrying-load-component';
import { SpillLoadComponent } from '../attachment/spill-load-component';
import { CurrentAtLocationComponent } from '../location/currently-at-location-component';
import { MapComponent } from '../location/map-component';
import { LocationReviewComponent } from '../location/location-review-component';
import { IdentifyLocationComponent } from '../location/identify-location-component';
import { DateTimeComponent } from '../datetime/datetime-component';
import { IntroductionComponent as TravelDirectionIntroduction } from '../travel-direction/introduction-component';
import { RoadTypesComponent } from '../travel-direction/road-types-component';
import { IntersectionsCrossingsComponent } from '../travel-direction/intersections-crossings-component';
import { RoadCharacteristicsComponent } from '../travel-direction/road-characteristics-component';
import { DirectionComponent } from '../travel-direction/direction-component';
import { ParkingLotomponent } from '../travel-direction/parking-lot-component';
import { PassengerInformationComponent } from '../passengers/passenger-information-component';
import { DriverComponent } from '../driver-vehicle/driver-component';
import { VehicleComponent } from '../driver-vehicle/vehicle-component';
import { DriverVehicleIntroductionComponent } from '../driver-vehicle/driver-vehicle-introduction-component';
import { IntroductionComponent as MediaIntroduction } from '../media/introduction-component';
import { MediaManagerComponent } from '../media/media-manager-component';
import { ContributingFactorsIntroductionComponent } from '../contributing-factors/contributing-factors-introduction-component';
import { RoadSurfaceConditionsComponent } from '../contributing-factors/road-surface-conditions-component';
import { WeatherConditionsComponent } from '../contributing-factors/weather-conditions-component';
import { LightConditionsComponent } from '../contributing-factors/light-conditions-component';
import { TrafficControlDeviceComponent } from '../contributing-factors/traffic-control-device-component';
import { VehicleConditionComponent } from '../contributing-factors/vehicle-condition-component';
import { ReviewComponent } from '../review/review-component';
import { Collision, FormStep } from '../../models/collision';
import ReviewCompleteComponent from '../review/review-complete-component';
import { displaySection } from '../../common/ui-library';
import { TermsAndConditionsComponent } from '../onboarding/terms-and-conditions-component';
import { RegisterComponent } from '../onboarding/register-component';
import { EligibilityIntroductionComponent } from '../eligibility/eligibility-introduction';
import { isUserLoggedIn } from '../../state/slices/authSlice';
import { DamageIntroductionComponent } from '../damage/damage-introduction-component';
import { FirstPointOfImpactComponent } from '../damage/first-point-of-impact-component';
import { ErrorComponent } from '../error/error-component';

interface WizardComponent {
  name: string;
  pageId: string;
  stepNumber?: number;
  totalSteps: number;
  component: JSX.Element;
  showBackLink: boolean;
  recalculateSteps?: () => void;
}

export const getReturnToValue = (): string => {
  if (window.location.hash) {
    if (window.location.hash.indexOf('?') !== -1) {
      const fragment = window.location.hash.split('?')[1] || '';
      const param = new URLSearchParams(fragment);
      const returnTo = param.get('returnTo');
      if (returnTo) return returnTo;

      return '';
    } else return '';
  }
  return '';
};

export const IsInReviewEditMode = (): boolean => {
  return getReturnToValue() === 'review';
};

interface WizardComponentProps {
  components?: WizardComponent[];
}

const useAppDispatch = () => useDispatch<AppDispatch>();

export const WizardComponent = ({ components }: WizardComponentProps) => {
  const csrService = useCsrService();
  const dispatch = useAppDispatch();

  const stateCollision = useSelector((state: RootState) => state.collision);
  const stateAuth = useSelector((state: RootState) => state.auth);
  const stateConfiguration = useSelector(
    (state: RootState) => state.configuration,
  );

  const defaultComponent = '#home';

  const getHash = () => {
    if (window.location.hash) {
      if (window.location.hash.indexOf('?') !== -1) {
        const found = window.location.hash.substring(
          0,
          window.location.hash.indexOf('?'),
        );
        return found;
      } else return window.location.hash;
    }
    return window.location.hash;
  };

  const [isInvalidStep, setIsInvalidStep] = useState(false);

  const [currentHash, setCurrentHash] = useState(getHash());
  const [returnToValue, setReturnToValue] = useState<string | null>(null);
  const returnToRef = useRef(returnToValue);

  useEffect(() => {
    setReturnToValue(getReturnToValue());
  }, [currentHash]);

  useEffect(() => {
    returnToRef.current = returnToValue;
  }, [returnToValue]);

  const [totalSteps, setTotalSteps] = useState(9);

  const [currentCollision, setCurrentCollision] = useState<Collision | null>(
    null,
  );

  const currentCollisionRef = useRef(currentCollision);

  const handleHashChange = () => {
    const newHash = getHash();
    setCurrentHash(newHash);

    window.scrollTo(0, 0);
  };

  useEffect(() => {
    currentCollisionRef.current = currentCollision;
  }, [currentCollision]);

  useEffect(() => {
    if (stateCollision.collision) {
      setCurrentCollision(stateCollision.collision);
    }
  }, [stateCollision]);

  useEffect(() => {
    dispatch(fetchConfiguration(csrService));
  }, [dispatch]);

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleGoToReview = () => {
    handleNavigation('#review');
  };

  const handleNavigation = (newComponent: string) => {
    let newHash = returnToRef.current
      ? `${newComponent}?returnTo=${returnToRef.current}`
      : `${newComponent}`;

    if (returnToRef.current) {
      const stepCompleted = wizardComponents.find(
        (component) => component.pageId === getHash(),
      )?.stepNumber;

      const nextStep = wizardComponents.find(
        (component) => component.pageId === newComponent,
      )?.stepNumber;

      if (stepCompleted != nextStep) {
        if (
          currentCollisionRef.current &&
          currentCollisionRef.current.metadata &&
          currentCollisionRef.current.metadata.formState
        ) {
          const state = currentCollisionRef.current.metadata.formState.find(
            (x) => x.step === nextStep,
          );
          console.log('State', state?.completed);
          if (state && state.completed) {
            newHash = `#${returnToRef.current}`;
          }
        }
      }
    }
    // Only update the hash here, let the useEffect handle the state update
    if (window.location.hash !== newHash) {
      window.location.hash = newHash;
    }
  };

  const handleCollisionSave = () => {
    if (isUserLoggedIn(stateAuth)) {
      console.log('Collision save');
      dispatch(publishCollision({ csrService }));
    }
  };

  const handleUpdateMetadata = (
    collision: Collision,
    stepId: number,
    completed: boolean,
    inProgress: boolean,
    skipped: boolean = false,
  ): FormStep[] | undefined => {
    console.log('Update metadata');

    const getNextStepNumber = (stepId: number): number => {
      if (
        stateCollisionRef.current.collision.collisionType &&
        isHitAndRun(
          stateCollisionRef.current.collision.collisionType,
          stateConfiguration,
        )
      ) {
        if (stepId == 6 || stepId == 8) {
          return stepId + 2;
        }
      }
      return stepId + 1;
    };

    if (collision) {
      let updatedSteps = collision.metadata!.formState.map((step: FormStep) =>
        step.step === stepId
          ? {
              ...step,
              completed: completed,
              inProgress: inProgress,
              skipped: skipped,
            }
          : step,
      );

      if (completed || skipped) {
        console.log('Step completed');
        updatedSteps = updatedSteps.map((step: FormStep) =>
          step.step === getNextStepNumber(stepId) && !step.completed
            ? {
                ...step,
                completed: false,
                inProgress: true,
                skipped: false,
              }
            : step,
        );
      }

      return updatedSteps;
    }

    return undefined;
  };

  const handleInitializeState = () => {
    if (!stateCollision.collision.id) {
      dispatch(publishCollision({ csrService }));
    }
  };

  const handleErrorNotification = (errorText: string) => {
    dispatch(
      pushNotification({
        body: errorText,
        notificationType: NotificationType.importantValidationError,
      }),
    );
  };

  const defaultNavigationConfiguration = {
    goToReview: handleGoToReview,
    navigate: handleNavigation,
    save: handleCollisionSave,
    errorNotification: handleErrorNotification,
    updateMetadata: undefined,
  };

  const stateCollisionRef = useRef(stateCollision);

  useEffect(() => {
    stateCollisionRef.current = stateCollision;
  }, [stateCollision]);

  useEffect(() => {
    if (stateCollision.collision.collisionType) {
      if (
        isHitAndRun(stateCollision.collision.collisionType, stateConfiguration)
      ) {
        setTotalSteps(7);
      } else {
        setTotalSteps(9);
      }

      wizardComponents.forEach((component) => {
        if (component.recalculateSteps) {
          component.stepNumber = component.recalculateSteps()!;
        }
      });
    }
  }, [stateCollisionRef.current.collision.collisionType]);

  const recalculateDriverVehicleSteps = () => {
    if (
      isHitAndRun(
        stateCollisionRef.current.collision.collisionType!,
        stateConfiguration,
      )
    ) {
      return 7;
    }

    return 8;
  };

  const [wizardComponents, setWizardComponents] = useState<WizardComponent[]>(
    components ?? [
      {
        name: 'TermsAndConditions',
        pageId: '#home',
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <TermsAndConditionsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '#eligibility-introduction' }],
            }}
          />
        ),
      },
      {
        name: 'EligibilityIntroduction',
        pageId: '#eligibility-introduction',
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <EligibilityIntroductionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '#eligibility' }],
            }}
          />
        ),
      },
      {
        name: 'Eligibility',
        pageId: '#eligibility',
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <EligibilityComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '#eligibility-success' }],
            }}
          />
        ),
      },
      {
        name: 'EligibilitySuccess',
        pageId: '#eligibility-success',
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <EligibilitySuccessComponent
            keycloakCallbackPath="dashboard"
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '' }],
            }}
          />
        ),
      },
      {
        name: 'Register',
        pageId: '#register',
        totalSteps: totalSteps,
        showBackLink: false,
        component: (
          <RegisterComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '' }],
            }}
          />
        ),
      },
      {
        name: 'CollisionIdentification',
        pageId: '#collision-identification',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <CollisionTypeComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              initializeState: handleInitializeState,
              pagingConfiguration: [
                { animal: '#animal' },
                { hitandrun: '#hitandrun' },
                { objectonroadway: '#object-on-roadway' },
                { rightsideroad: '#right-side-road' },
                { leftsideroad: '#left-side-road' },
              ],
            }}
          />
        ),
      },
      {
        name: 'Animal',
        pageId: '#animal',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <AnimalComponet
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#location-check' }],
            }}
          />
        ),
      },
      {
        name: 'HitAndRun',
        pageId: '#hit-and-run',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <ParkedHitAndRunComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#location-check' }],
            }}
          />
        ),
      },
      {
        name: 'ObjectOnRoadway',
        pageId: '#object-on-roadway',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <ObjectOnRoadwayComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#location-check' }],
            }}
          />
        ),
      },
      {
        name: 'LeftSideRoad',
        pageId: '#left-side-road',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <SideOfRoadComponent
            side="Left"
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#location-check' }],
            }}
          />
        ),
      },
      {
        name: 'RightSideRoad',
        pageId: '#right-side-road',
        stepNumber: 1,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <SideOfRoadComponent
            side="Right"
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 1,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#location-check' }],
            }}
          />
        ),
      },

      {
        name: 'CurrentlyAtLocation',
        pageId: '#location-check',
        stepNumber: 2,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <CurrentAtLocationComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 2,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  yes: '#identify-location',
                  no: '#map',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'IdentifyLocation',
        pageId: '#identify-location',
        stepNumber: 2,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <IdentifyLocationComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 2,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  yes: '#map',
                  no: '#map',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'Map',
        pageId: '#map',
        stepNumber: 2,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <MapComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 2,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  default: '#location-review',
                },
              ],
            }}
          />
        ),
      },

      {
        name: 'LocationReview',
        pageId: '#location-review',
        stepNumber: 2,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <LocationReviewComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 2,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                {
                  default: '#date-time',
                },
              ],
            }}
          />
        ),
      },

      {
        name: 'DateTime',
        pageId: '#date-time',
        stepNumber: 3,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DateTimeComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 3,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                { default: '#direction-road-details-introduction' },
              ],
            }}
          />
        ),
      },

      {
        name: 'IntroScreen',
        pageId: '#direction-road-details-introduction',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <TravelDirectionIntroduction
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                { default: '#direction-of-travel', parking: '#parking-lot' },
              ],
            }}
          />
        ),
      },
      {
        name: 'ParkingLot',
        pageId: '#parking-lot',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <ParkingLotomponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                { yes: '#intersections-crossings', no: '#road-types' },
              ],
            }}
          />
        ),
      },
      {
        name: 'DirectionOfTravel',
        pageId: '#direction-of-travel',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DirectionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [{ default: '#road-types' }],
            }}
          />
        ),
      },
      {
        name: 'RoadTypes',
        pageId: '#road-types',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <RoadTypesComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [{ default: '#intersections-crossings' }],
            }}
          />
        ),
      },

      {
        name: 'IntersectionsAndCrossings',
        pageId: '#intersections-crossings',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <IntersectionsCrossingsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                {
                  default: '#road-characteristics',
                  hitandrunyes: '#damage-introduction',
                  hitandrunno: '#road-characteristics',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'RoadCharacterstics',
        pageId: '#road-characteristics',
        stepNumber: 4,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <RoadCharacteristicsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 4,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#damage-introduction' }],
            }}
          />
        ),
      },
      {
        name: 'DamageIntroduction',
        pageId: '#damage-introduction',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DamageIntroductionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  default: '#damage',
                },
              ],
            }}
          />
        ),
      },

      {
        name: 'Damage',
        pageId: '#damage',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DamageComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  sticker: '#first-point-of-impact',
                  attachment: '#indicate-attachment-type',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'IndicateTypeOfAttachment',
        pageId: '#indicate-attachment-type',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <AttachmentTypeComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [{ default: '#carrying-load' }],
            }}
          />
        ),
      },
      {
        name: 'CarryingLoad',
        pageId: '#carrying-load',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <CarryingLoadComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  yes: '#load-details',
                  no: 'first-point-of-impact',
                  na: '#first-point-of-impact',
                  done: '#first-point-of-impact',
                },
              ],
            }}
          />
        ),
      },

      {
        name: 'LoadDetails',
        pageId: '#load-details',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <SpillLoadComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  default: '#first-point-of-impact',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'FirstPointOfImpact',
        pageId: '#first-point-of-impact',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <FirstPointOfImpactComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              stageCompleted: false,
              updateMetadata: handleUpdateMetadata,
              pagingConfiguration: [{ default: '#damage-sticker' }],
            }}
          />
        ),
      },
      {
        name: 'DamageSticker',
        pageId: '#damage-sticker',
        stepNumber: 5,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DamageStickerComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 5,
              stageCompleted: true,
              updateMetadata: handleUpdateMetadata,
              pagingConfiguration: [
                { default: '#media-introduction', manager: '#media' },
              ],
            }}
          />
        ),
      },

      {
        name: 'MediaIntroduction',
        pageId: '#media-introduction',
        stepNumber: 6,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <MediaIntroduction
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 6,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  default: '#media',
                  skip: '#contributing-factors-introduction',
                  hitandrun: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'MediaManagement',
        pageId: '#media',
        stepNumber: 6,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <MediaManagerComponent
            maxNumber={25}
            key={'poi'} // we are using the same component so we need to change the key to force a re-render
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 6,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                {
                  done: '#contributing-factors-introduction',
                  hitandrun: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },

      {
        name: 'ContributingFactorsIntroduction',
        pageId: '#contributing-factors-introduction',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <ContributingFactorsIntroductionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  weather: '#weather-conditions',
                  light: '#light-conditions',
                  road: '#road-surface-conditions',
                  traffic: '#traffic-control-device',
                  vehicle: '#vehicle-condition',
                  na: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'WeatherConditionsComponent',
        pageId: '#weather-conditions',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <WeatherConditionsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  light: '#light-conditions',
                  road: '#road-surface-conditions',
                  traffic: '#traffic-control-device',
                  vehicle: '#vehicle-condition',
                  default: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'LightConditionsComponent',
        pageId: '#light-conditions',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <LightConditionsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  road: '#road-surface-conditions',
                  traffic: '#traffic-control-device',
                  vehicle: '#vehicle-condition',
                  default: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'RoadSurfaceConditionsComponent',
        pageId: '#road-surface-conditions',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <RoadSurfaceConditionsComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  traffic: '#traffic-control-device',
                  vehicle: '#vehicle-condition',
                  default: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'TrafficControlDeviceComponent',
        pageId: '#traffic-control-device',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <TrafficControlDeviceComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [
                {
                  vehicle: '#vehicle-condition',
                  default: '#driver-vehicle-introduction',
                },
              ],
            }}
          />
        ),
      },
      {
        name: 'VehicleConditionComponent',
        pageId: '#vehicle-condition',
        stepNumber: 7,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <VehicleConditionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 7,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                { default: '#driver-vehicle-introduction' },
              ],
            }}
          />
        ),
      },

      {
        name: 'DriverVehicleIntroduction',
        pageId: '#driver-vehicle-introduction',
        stepNumber: 8,
        totalSteps: totalSteps,
        showBackLink: true,
        recalculateSteps: recalculateDriverVehicleSteps,
        component: (
          <DriverVehicleIntroductionComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 8,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [{ default: '#driver' }],
            }}
          />
        ),
      },
      {
        name: 'Driver',
        pageId: '#driver',
        stepNumber: 8,
        recalculateSteps: recalculateDriverVehicleSteps,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <DriverComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 8,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: false,
              pagingConfiguration: [{ default: '#vehicle' }],
            }}
          />
        ),
      },
      {
        name: 'Vehicle',
        pageId: '#vehicle',
        stepNumber: 8,
        recalculateSteps: recalculateDriverVehicleSteps,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <VehicleComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 8,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [
                { default: '#passenger-and-safety-info', hitandrun: '#review' },
              ],
            }}
          />
        ),
      },

      {
        name: 'PassengerAndSafetyInfo',
        pageId: '#passenger-and-safety-info',
        stepNumber: 9,
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <PassengerInformationComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 9,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#review' }],
            }}
          />
        ),
      },

      {
        name: 'Review',
        pageId: '#review',
        totalSteps: totalSteps,
        showBackLink: true,
        component: (
          <ReviewComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              stepId: 10,
              updateMetadata: handleUpdateMetadata,
              stageCompleted: true,
              pagingConfiguration: [{ default: '#review-complete' }],
            }}
          />
        ),
      },
      {
        name: 'ReviewComplete',
        pageId: '#review-complete',
        totalSteps: totalSteps,
        showBackLink: false,
        component: (
          <ReviewCompleteComponent
            navigationConfiguration={{
              ...defaultNavigationConfiguration,
              pagingConfiguration: [{ default: '#next' }],
            }}
          />
        ),
      },
    ],
  );

  const [currentComponent, setCurrentComponent] = useState<JSX.Element>();
  const [stepNumber, setStepNumber] = useState<number | undefined>(undefined);
  const [showBackLink, setShowBackLink] = useState(true);

  const handleBackNavigation = () => {
    dispatch(removeAllValidationErrorNotifications());
    window.history.back();
  };

  useEffect(() => {
    let newComponent: WizardComponent | undefined;
    if (currentHash === '') {
      newComponent = wizardComponents.find(
        (x) => x.pageId === defaultComponent,
      )!;
    } else {
      newComponent =
        wizardComponents.find((x) => x.pageId === currentHash) ?? undefined;
    }

    if (newComponent && newComponent.component) {
      setIsInvalidStep(false);
      setShowBackLink(
        newComponent && newComponent!.showBackLink != undefined
          ? newComponent!.showBackLink
          : true,
      );
      setCurrentComponent(newComponent?.component);
      setStepNumber(newComponent ? newComponent.stepNumber : undefined);
    } else {
      setIsInvalidStep(true);
    }
  }, [currentHash]);

  return (
    <div className="contentbox">
      <GoASpacer hSpacing={'xl'} />
      {isInvalidStep ? (
        <ErrorComponent />
      ) : (
        <div className="contentcolumn">
          <div
            style={{
              display: displaySection(showBackLink),
            }}
          >
            <GoASpacer vSpacing="m" />
            <a
              className="back-link"
              data-testid="back-link"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                handleBackNavigation();
              }}
            >
              Back
            </a>
          </div>
          <GoASpacer vSpacing="2xl" />
          <div className="step-label">
            {stepNumber && `step ${stepNumber} of ${totalSteps}`}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {currentComponent}
          </div>
          <GoASpacer vSpacing="3xl" />
        </div>
      )}
    </div>
  );
};
